import React, { Component } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { LuArrowLeft } from 'react-icons/lu';

import Tooltip from '../../../../../controls/common/tooltip';

import AliasWatch from '../../../../session/aliasWatch';
import WidgetStatus from '../../../common/widgetStatus';
import StatusPill from '../../components/statusPill';
import CellActions from '../cells/cellActions';
import CellSections from '../cells/cellSections';
import ObjectTree from './objectTree';
import { ArtifactPane, MetaPane, SessionPane, normalizeZoom } from './panes';
import {
  NODE_META,
  artifactNodeId,
  buildCellTree,
  fileContextFor,
  findNode,
  inputAffordance,
  sessionNodeId,
  sessionState,
} from '../../domain/cellObjects';
import { hasFullScope } from '../../common/scope';

import Preferences from 'context/preferences';

import './cell.css';

/**
 * What "this cell changed" MEANS, as one comparable string.
 *
 * The store rebuilds itself from the network on every session event, so an untouched cell arrives
 * as a NEW OBJECT several times a minute. Comparing prop identity therefore reloads both threads
 * because something elsewhere in the session moved, which is what made this surface strobe.
 *
 * `rev` leads and is on its own the whole answer for live data: it is the graph element's own
 * revision, the server bumps it on every mutation that touches the vertex, and the orchestrator
 * writes a cell exactly once per tick — one `set_vertex_props`, at the end, AFTER every meta-thread
 * record that tick produced. So a rev the browser has not seen before means the records behind it
 * are already on the thread, and a rev it has seen means there are none.
 *
 * The three fields after it are what `rev` would say if it were absent — a cell inserted locally
 * carries none until the next reload — and they are exactly the props the orchestrator commits that
 * this surface reads: `status` (the pane head's pill), `log` (the object tree, and every run's state
 * and exit), `input_needed` (which question is open, and so which control is live). `usage` is the
 * fourth committed prop and is deliberately absent: nothing here renders it, and it only ever moves
 * in the same write as a log entry closing.
 */
function cellSignature(cell) {
  const props = (cell && cell.props) || {};
  const log = (props.log || [])
    .map(entry => [entry.step, entry.run, entry.session, entry.state, entry.label].join(':'))
    .join('|');
  return JSON.stringify([cell && cell.rev, props.status, log, props.input_needed || null]);
}

/**
 * Fold a freshly-read NEWEST page into what the pane already holds.
 *
 * A reload reads the tail, so assigning it outright throws away every page the reader pulled in with
 * "load older" — and under a live tail that would happen on each item an agent writes, which makes
 * reading a long run's history while it runs impossible. Anything held that the fresh page does not
 * contain is older than the fresh page and stays; the fresh page is the tail and is authoritative
 * for the tail, so an item edited or removed there is still corrected.
 *
 * The cursor follows from that: it means "older than the oldest item held". Keeping any held item
 * keeps that boundary where it was, so the existing cursor still points just past it; keeping none
 * makes the fresh page the whole of what is held, and its cursor the boundary.
 *
 * @param {Array|null} held    - items on screen, or null for a thread not yet read
 * @param {string|null} cursor - the cursor those items were paged with
 * @param {{ items: Array, cursor: string|null }} page - the newest page, just read
 */
function foldNewest(held, cursor, page) {
  if (!held) return { items: page.items, cursor: page.cursor };
  const fresh = new Set(page.items.map(item => item.id));
  const older = held.filter(item => !fresh.has(item.id));
  return {
    items: [...older, ...page.items],
    cursor: older.length > 0 ? cursor : page.cursor
  };
}

/**
 * The blob-level artifact zoom, read once on mount.
 *
 * Reading size is a property of the reader, not of any one file, so it is stored per blob rather
 * than per artifact — see the DEFAULTS comment in context/preferences.js. A missing scope (the pane
 * reachable from tests, or an embedding that has none to give) reads as an empty scope, which
 * `Preferences.get` resolves against the user-level default — always `ARTIFACT_ZOOM_DEFAULT` — so
 * the surface still renders and still zooms, it just does not persist. `normalizeZoom` (panes.js) is
 * the one clamp for this value, shared with ArtifactPane, so a corrupt or out-of-range stored value
 * is rejected the same way on both sides.
 */
function initialArtifactZoom(scope) {
  return normalizeZoom(Preferences.get('blob:workflow.artifactZoom', scope));
}

// A range's onChange fires per pixel dragged, so persisting on every tick would mean ~50 synchronous
// localStorage writes (and as many Preferences._notify() broadcasts) for one 50%->300% drag. The
// live value still updates every tick (see handleArtifactZoomChange) so the slider stays smooth;
// only the write is debounced, leading+trailing: the first tick of a drag (or a single keyboard
// nudge) persists immediately, so nothing feels laggy, and later ticks in the same drag coalesce
// into one further write of the settled value once the drag pauses.
const ARTIFACT_ZOOM_PERSIST_DEBOUNCE_MS = 300;

/**
 * CellDetailSurface — one cell, everything it owns, and the controls that drive it.
 *
 * The mission map answers "what is the shape of this work"; this answers "what is happening inside
 * this piece of it", which is a different enough question to deserve the whole widget rather than a
 * dock beside the graph. A back control returns to the map.
 *
 * Left: the object tree. Right: whatever the selection is. The meta-thread is the default because it
 * is the surface a person actually drives the cell from — the sessions are the raw material.
 *
 * Props:
 *   cell      — cell vertex object from the store
 *   missionId — string
 *   client    — MissionsClient instance
 *   templates — store.templates ({ workflows, workspaces, agents } palettes), passed through to
 *               CellSections whole — see the comment at its call site below for why not rebuilt
 *   onBack       — () => void — back to the mission map
 *   onApplyLocal — (updater) => void; reflect an edit in the store without a reload
 *   canEdit   — boolean (default true) — false on a committed revision, a closed session, or with
 *               no write access. Reads are untouched: the tree, both threads, every artifact and
 *               every past question stay exactly as they are. What goes is the ability to answer
 *               the open one and the Actions section.
 *   scope     — { orgId, blobId } | undefined — the artifact zoom's persistence scope. Absent when
 *               the caller has none to give (e.g. a test rendering this surface directly), or
 *               carrying only one of the two keys (see hasFullScope) — either way the pane still
 *               zooms, it just does not persist across mounts.
 */
class CellDetailSurface extends Component {
  // This surface is handed a client, not the session context, so it holds its own watch over the same store.
  objects = new AliasWatch(() => this.props.client.objects(), () => { if (this._mounted) this.forceUpdate(); });

  state = {
    selectedId: NODE_META,
    metaItems: null,
    metaLoading: true,
    metaCursor: null,
    metaOlderLoading: false,
    artifactValue: null,
    artifactFailed: false,
    intendBusy: false,
    intendError: null,
    artifactZoom: initialArtifactZoom(this.props.scope)
  };

  componentDidMount() {
    this._mounted = true;
    this.loadMeta();
    this.watchOpenThread();
    this.objects.commit();
  }

  componentDidUpdate(prevProps) {
    // A NEW OBJECT is not news; a new signature is. See `cellSignature` for what counts as one and
    // why the reference test this replaced fired on every session event in the whole session.
    // Only the meta-thread: a run's transcript is loaded by the pane that shows it, keyed by the
    // run's own alias, so a cell prop moving says nothing about it and costs no read.
    if (cellSignature(prevProps.cell) !== cellSignature(this.props.cell)) {
      this.loadMeta();
    }
    // The run log is a prop, so the tree can grow a node — and the watched alias move — without the
    // selection changing.
    this.watchOpenThread();
    this.objects.commit();
  }

  componentWillUnmount() {
    this._mounted = false;
    this.unwatchThread();
    if (this._tailTimer) clearTimeout(this._tailTimer);
    if (this._zoomPersistTimer) {
      clearTimeout(this._zoomPersistTimer);
      // A pending trailing write means the drag's SETTLED value has not reached Preferences yet —
      // only the leading tick has. Dropping it here rather than flushing would leave whatever the
      // FIRST tick of the drag wrote as the remembered zoom, not what the user left the slider at.
      if (hasFullScope(this.props.scope)) this.persistArtifactZoom(this.state.artifactZoom);
    }
    this.objects.release();
  }

  // -- live tail -----------------------------------------------------------------------------
  //
  // The cell's own signature cannot see a run's transcript: a worker posts items for the whole
  // length of a run and the vertex is written only when that run opens and closes, so keying the
  // reload on the cell alone would leave an agent talking to an empty pane for ten minutes. Thread
  // items raise their own event, per alias, and this listens to the one alias the open pane reads.

  /**
   * The META-thread, and only that — the one this surface loads for itself.
   *
   * A run's transcript is watched by the pane that shows it (common/threadView.js), which loads,
   * pages and tails its own thread. Watching it here as well would read the same page twice for
   * every item that lands, and the two copies would race to be the one on screen.
   */
  openThreadAlias() {
    return this.tree().meta.alias;
  }

  watchOpenThread() {
    const alias = this.openThreadAlias();
    if (alias === this._watchedAlias) return;
    this.unwatchThread();
    this._watchedAlias = alias;
    this.props.client.watchThread(alias, this.handleThreadItem);
  }

  unwatchThread() {
    if (this._watchedAlias) this.props.client.unwatchThread(this._watchedAlias, this.handleThreadItem);
    this._watchedAlias = null;
  }

  // One poll delivers a whole batch of events and fires this once per item, so the burst is
  // collapsed onto the next tick — a page read per batch, not per item.
  handleThreadItem = () => {
    if (this._tailTimer) return;
    this._tailTimer = setTimeout(() => {
      this._tailTimer = null;
      if (!this._mounted) return;
      this.loadMeta();
    }, 0);
  };

  node() {
    return findNode(this.tree(), this.state.selectedId);
  }

  tree() {
    const { cell, missionId, client } = this.props;
    return buildCellTree({
      missionId,
      cellId: cell.element_id,
      log: (cell.props || {}).log,
      aliases: client.listCellObjectAliases(missionId, cell.element_id)
    });
  }

  // Both threads page NEWEST-first and then present oldest-first, so opening a long-running cell
  // costs one page rather than its whole history. The server's default page is ten items, which is
  // why an un-paged read looked complete and was not.
  //
  // Neither says `loading` while there is something on screen. The banner belongs to a read the
  // person is waiting on — opening the cell, opening a run — and a read that REPLACES items already
  // rendered is not one: announcing it strobes a spinner over a feed the reader is mid-sentence in.
  // Which of the two a call is, is not a flag the caller passes: the state already knows, because
  // "is anything on screen" is exactly "are there items for this thread".
  //
  // Each read is tagged, and a response whose tag is no longer current is dropped. Refreshes now
  // overlap selection changes — a tail event can land mid-switch — and the last request to be MADE
  // must be the one that wins, not the last to come back.
  loadMeta = async () => {
    const { client, missionId, cell } = this.props;
    if (this.state.metaItems === null) this.setState({ metaLoading: true });
    const read = {};
    this._metaRead = read;
    const page = await client.pageMetaThread(missionId, cell.element_id);
    if (!this._mounted || this._metaRead !== read) return;
    this.setState(state => {
      const folded = foldNewest(state.metaItems, state.metaCursor, page);
      return { metaItems: folded.items, metaCursor: folded.cursor, metaLoading: false };
    });
  };

  loadOlderMeta = async () => {
    const { client, missionId, cell } = this.props;
    this.setState({ metaOlderLoading: true });
    const page = await client.pageMetaThread(missionId, cell.element_id, this.state.metaCursor);
    if (!this._mounted) return;
    this.setState(state => ({
      metaItems: [...page.items, ...(state.metaItems || [])],
      metaCursor: page.cursor,
      metaOlderLoading: false
    }));
  };

  // An artifact chip in the activity feed selects that artifact in the tree — the feed says what
  // was produced, the tree is where it lives, and they should be the same click.
  handleOpenArtifact = (alias) => this.handleSelect(artifactNodeId(alias));

  // ...and the same for a run: the feed says which run a record is about, the tree is where that
  // run's transcript lives. Offered only for the runs the tree HAS (see `renderPane`), so the chip
  // on a gate's record — a run that opens no session — stays inert instead of selecting nothing.
  handleOpenRun = (key) => this.handleSelect(sessionNodeId(key));

  // Opening an artifact ACTS on an object, so it loads it once; the pane shows its own loading state while
  // `artifactValue` is null. Tagged like loadMeta: the last selection to be MADE wins, not the last to land —
  // and the tag is set UNCONDITIONALLY, before the early return, so a selection that moves away from the
  // artifact (to the meta or a session node) also invalidates whatever load is still in flight for it.
  handleSelect = (selectedId) => {
    this.setState({ selectedId, artifactValue: null, artifactFailed: false }, async () => {
      const read = {};
      this._artifactRead = read;
      const node = this.node();
      if (!node || node.kind !== 'artifact') return;
      const value = await this.props.client.loadObject(node.alias);
      if (!this._mounted || this._artifactRead !== read) return;
      this.setState(value ? { artifactValue: value } : { artifactFailed: true });
    });
  };

  /**
   * Can this cell be asked anything? Only false in one window — a cell activated since the last
   * tick, whose meta-thread the orchestrator has not created yet — and a cell at rest is never in
   * it. Checked anyway, because the composer must not offer a post the platform would refuse.
   */
  canIntend() {
    const { client, missionId, cell } = this.props;
    if (!client || !client.canIntend) return true;
    return client.canIntend(missionId, cell.element_id);
  }

  /**
   * Ask a cell at rest to move, with whatever was typed as the reason.
   *
   * The message IS the intent's body, so it lands in the feed beside the outcome it causes, and the
   * reload is what puts it there — the record is the optimistic update, and it is the same one a
   * refresh recovers "pending" from, so nothing about the in-flight state is held in this tab.
   */
  handleIntend = async (verb, text, answers) => {
    const { client, missionId, cell } = this.props;
    // The run the ask is about, off the request the control was built from. An `answer` and a
    // `message` both name one; a resume or a restart carries whatever the request says.
    const request = (cell.props && cell.props.request) || {};
    this.setState({ intendBusy: true, intendError: null });
    try {
      await client.postIntent(missionId, cell.element_id, verb, text,
                              { step: request.step, run: request.run, answers });
    } catch (error) {
      if (this._mounted) this.setState({ intendBusy: false, intendError: (error && error.message) || String(error) });
      return false;
    }
    try {
      await this.loadMeta();
    } catch (error) {
      console.error(`Mission Control: reload after a send to ${cell.element_id} failed`, error);
    } finally {
      if (this._mounted) this.setState({ intendBusy: false });
    }
    return true;
  };

  // The same {api, revisionId, sessionId} the transcript view resolves its file blocks against —
  // an artifact's own bytes are fetched from the same place. Shared with SessionPane.fileContext.
  fileContext() {
    return fileContextFor(this.props.client);
  }

  // Owns the artifact zoom value — ArtifactPane is controlled (see panes.js), so this both updates
  // the state that flows back down as its `zoom` prop, on every tick, and persists the choice for
  // next time, debounced (see ARTIFACT_ZOOM_PERSIST_DEBOUNCE_MS above). A scope missing either half
  // is refused (see hasFullScope) rather than written somewhere a later read can never find — the
  // pane still zooms for this mount either way, it just does not persist.
  handleArtifactZoomChange = (zoom) => {
    this.setState({ artifactZoom: zoom });
    if (!hasFullScope(this.props.scope)) return;
    // Leading edge: nothing pending, so this tick starts a new burst — persist it right away.
    if (!this._zoomPersistTimer) this.persistArtifactZoom(zoom);
    clearTimeout(this._zoomPersistTimer);
    // Trailing edge: whichever tick is still pending once the burst goes quiet wins, since every
    // earlier trailing timer for this burst was cleared above.
    this._zoomPersistTimer = setTimeout(() => {
      this._zoomPersistTimer = null;
      this.persistArtifactZoom(zoom);
    }, ARTIFACT_ZOOM_PERSIST_DEBOUNCE_MS);
  };

  persistArtifactZoom(zoom) {
    Preferences.set('blob:workflow.artifactZoom', zoom, this.props.scope);
  }

  renderPane(tree) {
    const { cell, client } = this.props;
    const node = findNode(tree, this.state.selectedId);
    if (!node) return <WidgetStatus variant="light">Select something on the left.</WidgetStatus>;

    if (node.kind === 'session') {
      const state = sessionState(this.objects.read(node.alias).value);
      // No items, no cursor, no loading flags: the pane's view loads, pages and tails the run's
      // thread itself (common/threadView.js), which is the same code the thread widget runs. What
      // this surface still owns is the META-thread — records, not messages, and the one place a
      // person answers from.
      return (
        <SessionPane
          session={node}
          state={state}
          client={client}
          onResolved={() => this.forceUpdate()}
        />
      );
    }
    if (node.kind === 'artifact') {
      if (this.state.artifactFailed) {
        return <WidgetStatus variant="warning">Could not load this artifact.</WidgetStatus>;
      }
      return <ArtifactPane artifact={node} value={this.state.artifactValue}
                           fileContext={this.fileContext()}
                           zoom={this.state.artifactZoom}
                           onZoomChange={this.handleArtifactZoomChange} />;
    }
    return (
      <MetaPane
        items={this.state.metaItems}
        loading={this.state.metaLoading}
        loadingOlder={this.state.metaOlderLoading}
        onLoadOlder={this.state.metaCursor ? this.loadOlderMeta : null}
        onOpenArtifact={this.handleOpenArtifact}
        onOpenRun={this.handleOpenRun}
        runKeys={tree.sessions.map(session => session.key)}
        client={client}
        onResolved={() => this.forceUpdate()}
        cell={cell}
        affordance={inputAffordance(cell)}
        canEdit={this.props.canEdit}
        onIntend={this.canIntend() ? this.handleIntend : null}
        intendBusy={this.state.intendBusy}
          intendError={this.state.intendError}
      />
    );
  }

  render() {
    this.objects.begin();
    const { cell, onBack, missionId, client } = this.props;
    const canEdit = this.props.canEdit !== false;
    const props = cell.props || {};
    const tree = this.tree();
    // The same Container / Row / Col the cells surface uses, so this surface inherits the widget's
    // gutters by construction instead of by a hand-picked number: the widget body gives 15px, the
    // container adds 10px vertically and Bootstrap's 15px horizontally, the row cancels 15 and each
    // column re-adds it. Reproducing that arithmetic here is how the two surfaces drift apart.
    // Two columns, not three bands: the cell's own details belong WITH its object list, in one
    // card, because they answer the same question — what is this thing, and what does it own. A
    // full-width header band above both columns spent a row of the widget on four short values.
    //
    // The rail is `.detail-panel-col`, the app-wide property-panel column, so it is exactly as wide
    // as the cell panel this view is reached from and the eye lands in the same place.
    return (
      <Container fluid className="mc-cells-container mc-cell-detail">
        <Row className="mc-grid-row mc-cell-detail-body">
          <Col className="d-flex flex-column detail-panel-col">
            <Card className="mc-tree-card">
              <Card.Body>
                {/* The way back, at the LEADING edge — which is where a back control lives in every
                    nav bar, browser and list-detail screen there is, and where the eye goes to leave.
                    It was an ✕ at the trailing edge: the glyph that DISMISSES a panel, one screen
                    away from the dock where it does exactly that, and pointing nowhere.

                    `workflow-controls-pane-left` is the app's own left-hand header strip (the
                    editor's, the playground workspaces') and gives it the same 30×26 box as the
                    controls opposite, so the two levels' headers are one height and one control
                    size. The title clears it by `.mc-detail-head` — see cell.css. */}
                <span className="workflow-controls-pane-left">
                  <Tooltip text="Back to the mission">
                    <Button variant="light" onClick={onBack} aria-label="Back to the mission">
                      <LuArrowLeft />
                    </Button>
                  </Tooltip>
                </span>
                <Card.Text as="h6" className="mc-detail-head">Cell</Card.Text>
                <hr />
                <table className="properties">
                  <tbody>
                    <tr>
                      <td className="prop-name-column">Cell ID</td>
                      <td className="prop-value-column"><code>{cell.element_id}</code></td>
                    </tr>
                    <tr>
                      <td className="prop-name-column">Status</td>
                      <td className="prop-value-column"><StatusPill status={props.status} /></td>
                    </tr>
                  </tbody>
                </table>
                {/* The SAME three sections the mission dock renders, in the same order, with the
                    same collapse behaviour — one component rather than a second copy of them. This
                    screen used to write them out itself and drifted: Workspace was absent here
                    entirely, not read-only, and the other two could not be edited even while the
                    cell was a draft.

                    `templates` is passed through WHOLE, never rebuilt field-by-field: a rebuild is
                    exactly how this surface silently lost `agents` before (and how `setTemplates`
                    in domain/model.js lost it earlier still) — any key the picker adds next would
                    be dropped the same way. */}
                <CellSections cell={cell} missionId={missionId} client={client}
                  templates={this.props.templates}
                  onApplyLocal={this.props.onApplyLocal} canEdit={canEdit} />

                {/* The same actions the mission dock offers, minus the one that only navigates.
                    No `onOpen`: the dock's Answer/Continue card exists to bring you HERE, and from
                    here the timeline it points at is the pane already open, one click away in the
                    tree below. Everything that actually does something — Activate, Retry, Pause,
                    Resume, Restart — is identical on both surfaces, which is the point of
                    sharing the component. */}
                <CellActions
                  cell={cell} canEdit={canEdit} missionId={missionId} client={client}
                  onActionDone={this.loadMeta}
                  onApplyLocal={this.props.onApplyLocal}
                  // This screen has a composer at the foot of the meta-thread, so the verbs that
                  // can carry a reason are offered there rather than twice.
                  composer />

                <ObjectTree tree={tree} selectedId={this.state.selectedId}
                  onSelect={this.handleSelect} />
              </Card.Body>
            </Card>
          </Col>
          <Col className="d-flex flex-column">
            <Card className="mc-detail-card">
              <Card.Body>{this.renderPane(tree)}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default CellDetailSurface;
